import { mkdirSync } from "node:fs";
import path from "node:path";
import type { LogLevel, LogStoreConfig, LogStoreMode } from "@skaile/workspaces/types";
import { LogStore } from "./log-store.js";
import { NoOpSink } from "./sinks/no-op-sink.js";
import { SqliteSink } from "./sinks/sqlite-sink.js";
import { StdoutSink } from "./sinks/stdout-sink.js";

const VALID_MODES: LogStoreMode[] = ["persistent", "ephemeral", "off"];
const VALID_LEVELS: LogLevel[] = ["debug", "info", "warn", "error"];

export interface ResolveLogStoreConfigInput {
  sessionId: string;
  workspacePath?: string;
  yamlBlock?: Partial<{
    mode: LogStoreMode;
    level: LogLevel;
    retention: { maxEntries?: number; maxAgeDays?: number };
  }>;
}

export function resolveLogStoreConfig(input: ResolveLogStoreConfigInput): LogStoreConfig {
  const yaml = input.yamlBlock ?? {};
  const envMode = process.env.SKAILE_LOG_MODE as LogStoreMode | undefined;
  const envLevel = process.env.SKAILE_LOG_LEVEL as LogLevel | undefined;

  const mode = envMode ?? yaml.mode ?? "persistent";
  if (!VALID_MODES.includes(mode)) {
    throw new Error(`Invalid logging.mode: ${mode}`);
  }
  const level = envLevel ?? yaml.level ?? "info";
  if (!VALID_LEVELS.includes(level)) {
    throw new Error(`Invalid logging.level: ${level}`);
  }

  return {
    sessionId: input.sessionId,
    workspacePath: input.workspacePath,
    mode,
    level,
    retention: yaml.retention,
  };
}

export function createLogStoreFromConfig(cfg: LogStoreConfig): LogStore {
  const sinks = [];
  if (cfg.mode === "off") {
    sinks.push(new NoOpSink());
  } else if (cfg.mode === "ephemeral") {
    sinks.push(new SqliteSink({ path: ":memory:" }));
  } else {
    if (!cfg.workspacePath) {
      throw new Error("logging.mode=persistent requires workspacePath");
    }
    const dir = path.join(cfg.workspacePath, ".skaile");
    mkdirSync(dir, { recursive: true });
    const dbPath = path.join(dir, "logs.db");
    sinks.push(new SqliteSink({ path: dbPath }));
  }
  // Always include stdout for live container logs (dev-friendly).
  sinks.push(new StdoutSink());
  return new LogStore({
    sessionId: cfg.sessionId,
    level: cfg.level,
    sinks,
  });
}
